<template>
  <AppSidebar>
    <div class="space-y-10 pb-12">
      <!-- Page Header -->
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-8 pb-4">
        <div class="space-y-2">
            <div class="flex items-center gap-3 mb-1">
                <div class="w-2 h-2 bg-primary-500 rounded-full shadow-[0_0_12px_rgba(20,184,166,0.5)] animate-pulse"></div>
                <span class="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest">Contact Management</span>
            </div>
            <h1 class="text-4xl font-bold text-gray-900 dark:text-white uppercase tracking-tighter sm:text-5xl" style="font-family: Poppins, sans-serif">
                Contact <span class="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-blue-500">Transmission Hub</span>
            </h1>
            <p class="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-loose max-w-md">
                Configure your contact information, hero section, and social media links.
            </p>
        </div>
      </div>

      <!-- Main Interface -->
      <div class="bg-white dark:bg-gray-800/40 rounded-[40px] border border-gray-100 dark:border-gray-700/50 shadow-2xl shadow-gray-200/50 dark:shadow-none overflow-hidden backdrop-blur-xl">
        <!-- Tabs Implementation -->
        <div class="border-b border-gray-100 dark:border-gray-800/50 px-8 py-2 bg-gray-50/30 dark:bg-gray-900/40">
          <nav class="flex gap-8 overflow-x-auto custom-scrollbar">
            <button v-for="tab in tabs" :key="tab.id" 
              @click="activeTab = tab.id"
              class="relative py-4 text-[10px] font-black uppercase tracking-widest transition-all duration-300 group whitespace-nowrap"
              :class="activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'">
              {{ tab.name }}
              <div class="absolute bottom-0 left-0 h-0.5 bg-primary-500 transition-all duration-300"
                  :class="activeTab === tab.id ? 'w-full' : 'w-0 group-hover:w-1/2'"></div>
            </button>
          </nav>
        </div>

        <div class="p-8 sm:p-12">
          <!-- Loading State (Premium Spinner) -->
          <div v-if="loading" class="flex flex-col items-center justify-center py-24 space-y-4">
            <div class="relative w-16 h-16">
              <div class="absolute inset-0 border-4 border-primary-500/20 rounded-full"></div>
              <div class="absolute inset-0 border-4 border-primary-500 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <span class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Loading Settings...</span>
          </div>

          <div v-else class="animate-in fade-in slide-in-from-bottom-2 duration-500">
            <!-- Hero Configuration Tab -->
            <div v-if="activeTab === 'hero'" class="max-w-4xl space-y-10">
              <div class="flex items-center gap-4 mb-2">
                  <div class="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-2xl">
                    <SparklesIcon class="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Hero Section</h3>
                    <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Primary section heading and description</p>
                  </div>
              </div>

              <form @submit.prevent="saveSection('hero')" class="space-y-8">
                 <div class="group">
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Main Heading</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary-500 transition-colors">
                      <ChatBubbleBottomCenterTextIcon class="h-4 w-4" />
                    </div>
                    <input 
                      v-model="sections.hero.data.title" 
                      type="text" 
                      class="w-full pl-12 pr-6 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm"
                      placeholder="ENTER HUB HEADLINE"
                    />
                  </div>
                </div>

                 <div class="group">
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Sub-Heading</label>
                  <textarea 
                    v-model="sections.hero.data.subtitle" 
                    rows="4"
                    class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium tracking-wider text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm leading-relaxed"
                    placeholder="Describe your contact page message..."
                  ></textarea>
                </div>

                <div class="flex justify-end pt-4">
                  <button type="submit" 
                    class="group relative inline-flex items-center px-10 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                     <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                     Save Hero
                  </button>
                </div>
              </form>
            </div>

            <!-- Neural Linkage Tab -->
            <div v-if="activeTab === 'settings'" class="max-w-5xl space-y-12">
              <form @submit.prevent="saveSettings" class="space-y-12">
                <!-- Core Transmission Channels -->
                <div class="space-y-8">
                  <div class="flex items-center gap-4 mb-2">
                    <div class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
                      <GlobeAltIcon class="w-5 h-5 text-blue-500" />
                    </div>
                      <div>
                       <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Communication Channels</h3>
                       <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Primary communication settings</p>
                     </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Contact Email</label>
                      <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary-500 transition-colors">
                          <AtSymbolIcon class="h-4 w-4" />
                        </div>
                        <input v-model="settingsForm.contact_email" type="email" 
                          class="w-full pl-12 pr-6 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                      </div>
                    </div>

                     <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Phone Number</label>
                      <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary-500 transition-colors">
                          <PhoneIcon class="h-4 w-4" />
                        </div>
                        <input v-model="settingsForm.contact_phone" type="text" 
                          class="w-full pl-12 pr-6 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                      </div>
                    </div>

                     <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">WhatsApp Number</label>
                      <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary-500 transition-colors">
                          <DevicePhoneMobileIcon class="h-4 w-4" />
                        </div>
                        <input v-model="settingsForm.whatsapp_number" type="text" 
                          class="w-full pl-12 pr-6 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                      </div>
                    </div>

                     <div class="group">
                      <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Office Hours</label>
                      <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary-500 transition-colors">
                          <ClockIcon class="h-4 w-4" />
                        </div>
                        <input v-model="settingsForm.office_hours" type="text" 
                          class="w-full pl-12 pr-6 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                      </div>
                    </div>
                  </div>

                   <div class="group pt-4">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Physical Address</label>
                    <div class="relative">
                      <div class="absolute top-5 left-5 text-gray-400 group-focus-within:text-primary-500 transition-colors">
                        <MapPinIcon class="h-4 w-4" />
                      </div>
                      <textarea v-model="settingsForm.address" rows="3"
                        class="w-full pl-12 pr-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium tracking-wider text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm leading-relaxed"></textarea>
                    </div>
                  </div>
                </div>

                <!-- Neural Grid Hubs (Socials) -->
                <div class="space-y-8">
                  <div class="flex items-center gap-4 mb-2">
                    <div class="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
                      <SparklesIcon class="w-5 h-5 text-amber-500" />
                    </div>
                      <div>
                       <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Neural Linkage Hub</h3>
                       <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">External social media profiles</p>
                     </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <!-- FB Connector -->
                    <div class="p-6 bg-gray-50/30 dark:bg-gray-900/20 border border-gray-100/50 dark:border-gray-700/30 rounded-[32px] group relative overflow-hidden transition-all hover:border-blue-500/30">
                      <div class="absolute -right-4 -top-4 w-12 h-12 bg-blue-500/5 rounded-full blur-xl group-hover:bg-blue-500/10 transition-colors"></div>
                       <div class="flex flex-col gap-4 relative z-10 text-left">
                        <div class="text-[9px] font-bold text-blue-500 dark:text-blue-400 uppercase tracking-widest text-left">Facebook Page</div>
                        <input v-model="settingsForm.facebook_url" type="url" placeholder="https://..." class="w-full bg-transparent border-none focus:ring-0 p-0 text-[11px] font-mono text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-700 text-left" />
                      </div>
                    </div>

                    <!-- X Connector -->
                    <div class="p-6 bg-gray-50/30 dark:bg-gray-900/20 border border-gray-100/50 dark:border-gray-700/30 rounded-[32px] group relative overflow-hidden transition-all hover:border-gray-500/30">
                      <div class="absolute -right-4 -top-4 w-12 h-12 bg-gray-500/5 rounded-full blur-xl group-hover:bg-gray-500/10 transition-colors"></div>
                       <div class="flex flex-col gap-4 relative z-10 text-left">
                        <div class="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest text-left">X / Twitter</div>
                        <input v-model="settingsForm.twitter_url" type="url" placeholder="https://..." class="w-full bg-transparent border-none focus:ring-0 p-0 text-[11px] font-mono text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-700 text-left" />
                      </div>
                    </div>

                    <!-- LinkedIn Connector -->
                    <div class="p-6 bg-gray-50/30 dark:bg-gray-900/20 border border-gray-100/50 dark:border-gray-700/30 rounded-[32px] group relative overflow-hidden transition-all hover:border-blue-600/30 text-left">
                      <div class="absolute -right-4 -top-4 w-12 h-12 bg-blue-600/5 rounded-full blur-xl group-hover:bg-blue-600/10 transition-colors"></div>
                       <div class="flex flex-col gap-4 relative z-10 text-left">
                        <div class="text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest text-left">LinkedIn Profile</div>
                        <input v-model="settingsForm.linkedin_url" type="url" placeholder="https://..." class="w-full bg-transparent border-none focus:ring-0 p-0 text-[11px] font-mono text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-700 text-left" />
                      </div>
                    </div>

                    <!-- Instagram Connector -->
                    <div class="p-6 bg-gray-50/30 dark:bg-gray-900/20 border border-gray-100/50 dark:border-gray-700/30 rounded-[32px] group relative overflow-hidden transition-all hover:border-pink-500/30">
                      <div class="absolute -right-4 -top-4 w-12 h-12 bg-pink-500/5 rounded-full blur-xl group-hover:bg-pink-500/10 transition-colors"></div>
                       <div class="flex flex-col gap-4 relative z-10 text-left">
                        <div class="text-[9px] font-bold text-pink-500 dark:text-pink-400 uppercase tracking-widest text-left">Instagram Profile</div>
                        <input v-model="settingsForm.instagram_url" type="url" placeholder="https://..." class="w-full bg-transparent border-none focus:ring-0 p-0 text-[11px] font-mono text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-700 text-left" />
                      </div>
                    </div>

                    <!-- GitHub Connector -->
                    <div class="p-6 bg-gray-50/30 dark:bg-gray-900/20 border border-gray-100/50 dark:border-gray-700/30 rounded-[32px] group relative overflow-hidden transition-all hover:border-primary-500/30">
                      <div class="absolute -right-4 -top-4 w-12 h-12 bg-primary-500/5 rounded-full blur-xl group-hover:bg-primary-500/10 transition-colors"></div>
                       <div class="flex flex-col gap-4 relative z-10 text-left">
                        <div class="text-[9px] font-bold text-primary-500 dark:text-primary-400 uppercase tracking-widest text-left">GitHub Profile</div>
                        <input v-model="settingsForm.github_url" type="url" placeholder="https://..." class="w-full bg-transparent border-none focus:ring-0 p-0 text-[11px] font-mono text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-700 text-left" />
                      </div>
                    </div>

                    <!-- YouTube Connector -->
                    <div class="p-6 bg-gray-50/30 dark:bg-gray-900/20 border border-gray-100/50 dark:border-gray-700/30 rounded-[32px] group relative overflow-hidden transition-all hover:border-red-500/30">
                      <div class="absolute -right-4 -top-4 w-12 h-12 bg-red-500/5 rounded-full blur-xl group-hover:bg-red-500/10 transition-colors"></div>
                       <div class="flex flex-col gap-4 relative z-10 text-left">
                        <div class="text-[9px] font-bold text-red-500 dark:text-red-400 uppercase tracking-widest text-left">YouTube Channel</div>
                        <input v-model="settingsForm.youtube_url" type="url" placeholder="https://..." class="w-full bg-transparent border-none focus:ring-0 p-0 text-[11px] font-mono text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-700 text-left" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="flex justify-end pt-8 border-t border-gray-100 dark:border-gray-800 backdrop-blur-md">
                   <button type="submit" :disabled="saving" 
                      class="group relative inline-flex items-center px-12 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                      <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      <span v-if="saving" class="flex items-center gap-3">
                         <ArrowPathIcon class="animate-spin h-4 w-4" />
                        Saving Settings...
                      </span>
                      <span v-else>Save Contact Settings</span>
                    </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { 
  SparklesIcon, ChatBubbleBottomCenterTextIcon, GlobeAltIcon, 
  AtSymbolIcon, PhoneIcon, DevicePhoneMobileIcon, 
  ClockIcon, MapPinIcon, ArrowPathIcon 
} from '@heroicons/vue/24/outline'
import { useContentStore } from '@/stores/content'
import { useSettingsStore } from '@/stores/settings'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const contentStore = useContentStore()
const settingsStore = useSettingsStore()

const loading = ref(true)
const saving = ref(false)
const activeTab = ref('hero')

const tabs = [
  { id: 'hero', name: 'Hero Section' },
  { id: 'settings', name: 'Contact Settings' }
]

const settingsForm = ref({
  contact_email: '',
  contact_phone: '',
  whatsapp_number: '',
  address: '',
  office_hours: '',
  facebook_url: '',
  twitter_url: '',
  linkedin_url: '',
  instagram_url: '',
  github_url: '',
  youtube_url: ''
})

// Data loading
onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('contact'),
      settingsStore.fetchSettings()
    ])
    
    // Load settings into form
    settingsForm.value = {
      contact_email: settingsStore.settings.contact_email || '',
      contact_phone: settingsStore.settings.contact_phone || '',
      whatsapp_number: settingsStore.settings.whatsapp_number || '',
      address: settingsStore.settings.address || '',
      office_hours: settingsStore.settings.office_hours || '',
      facebook_url: settingsStore.settings.facebook_url || '',
      twitter_url: settingsStore.settings.twitter_url || '',
      linkedin_url: settingsStore.settings.linkedin_url || '',
      instagram_url: settingsStore.settings.instagram_url || '',
      github_url: settingsStore.settings.github_url || '',
      youtube_url: settingsStore.settings.youtube_url || ''
    }
    
    // Initialize sections if they don't exist
    if (!sections.value.hero) {
      sections.value.hero = { section: 'hero', data: { title: '', subtitle: '' } }
    }
  } catch (error) {
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
})

// Computed
const rawSections = computed(() => contentStore.pageSections.contact || [])
const sections = computed(() => {
  const map = {}
  rawSections.value.forEach(s => map[s.section] = JSON.parse(JSON.stringify(s)))
  
  // Defaults
  if (!map.hero) map.hero = { section: 'hero', data: { title: '', subtitle: '' } }
  
  return map
})

// Methods
const saveSection = async (sectionName) => {
  const section = sections.value[sectionName]
  try {
    if (section.id) {
      await contentStore.updatePageSection(section.id, {
        page: 'contact',
        section: sectionName,
        data: section.data
      })
    } else {
      await contentStore.createPageSection({
        page: 'contact',
        section: sectionName,
        data: section.data
      })
    }
    // Refresh
    await contentStore.fetchPageSections('contact')
  } catch (error) {
    console.error('Error saving section:', error)
  }
}

const saveSettings = async () => {
  saving.value = true
  try {
    await settingsStore.updateSettings(settingsForm.value)
  } catch (error) {
    console.error('Error saving settings:', error)
  } finally {
    saving.value = false
  }
}
</script>
